import { MemberRole } from '../types/organisation';

/** Platform-level org dashboard access (Super Admin login, org tab + org tools). */
export type PlatformOrgAdminRole = Extract<MemberRole, 'superAdmin' | 'admin'>;

/** App Content menu item ids that limited platform admins (`admin`) may open. */
export const LIMITED_ADMIN_APP_CONTENT_IDS = ['pins', 'moderation'] as const;

export type LimitedAdminAppContentId = (typeof LIMITED_ADMIN_APP_CONTENT_IDS)[number];

/** Paths where limited admins need full project nav (Pins / Moderation). */
const LIMITED_ADMIN_APP_CONTENT_PATHS = [
  '/split-view',
  '/pin-list-view',
  '/moderation-view',
] as const;

export function isSuperAdminUserRole(userRole?: string | null): userRole is 'superAdmin' {
  return userRole === 'superAdmin';
}

export function isPlatformOrgAdminUserRole(userRole?: string | null): userRole is 'admin' {
  return userRole === 'admin';
}

export function hasPlatformOrgAccess(userRole?: string | null): boolean {
  return isSuperAdminUserRole(userRole) || isPlatformOrgAdminUserRole(userRole);
}

/** Session role or a linked SuperAdmins/OrgAdmins claim from another login. */
export function userHasPlatformOrgAccess(user?: {
  userRole?: string | null;
  platformAdminRole?: string | null;
} | null): boolean {
  return hasPlatformOrgAccess(user?.userRole) || hasPlatformOrgAccess(user?.platformAdminRole);
}

export function getEffectivePlatformRole(user?: {
  userRole?: string | null;
  platformAdminRole?: string | null;
} | null): PlatformOrgAdminRole | undefined {
  if (isSuperAdminUserRole(user?.userRole) || isSuperAdminUserRole(user?.platformAdminRole)) {
    return 'superAdmin';
  }
  if (isPlatformOrgAdminUserRole(user?.userRole) || isPlatformOrgAdminUserRole(user?.platformAdminRole)) {
    return 'admin';
  }
  return undefined;
}

export function getDefaultSuperAdminLoginPath(userRole?: string | null): string {
  if (isPlatformOrgAdminUserRole(userRole)) {
    return '/super-admin-dashboard?tab=org';
  }
  return '/super-admin-dashboard?tab=org';
}

/**
 * OVERHEAR Admin: organisational tools use minimal top nav (theme + sign out).
 * On limited App Content surfaces (Pins / Moderation), full project nav is needed.
 */
export function shouldUseMinimalPlatformAdminNav(
  userRole?: string | null,
  pathname?: string | null
): boolean {
  if (!isPlatformOrgAdminUserRole(userRole)) return false;
  if (pathname && isLimitedAdminAppContentPath(pathname)) return false;
  return true;
}

export function isLimitedAdminAppContentPath(pathname: string): boolean {
  return LIMITED_ADMIN_APP_CONTENT_PATHS.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`)
  );
}

/** Super Admin Dashboard App Content tab (full or filtered). */
export function canAccessAppContentTab(userRole?: string | null): boolean {
  return hasPlatformOrgAccess(userRole);
}

/** Top-nav App Menu / Org Menu for platform super and org admins. */
export function canSeePlatformAppOrgMenus(userRole?: string | null): boolean {
  return hasPlatformOrgAccess(userRole);
}

/** Whether a platform role may see a given App Content menu item. */
export function canAccessAppContentItem(
  userRole?: string | null,
  itemId?: string | null
): boolean {
  if (!itemId) return false;
  if (isSuperAdminUserRole(userRole)) return true;
  if (isPlatformOrgAdminUserRole(userRole)) {
    return (LIMITED_ADMIN_APP_CONTENT_IDS as readonly string[]).includes(itemId);
  }
  return false;
}

export function canModerateRecordings(userRole?: string | null): boolean {
  return hasPlatformOrgAccess(userRole);
}

export function canEditTranscriptsAndPreviews(userRole?: string | null): boolean {
  return hasPlatformOrgAccess(userRole);
}

export function canDeleteProjects(userRole?: string | null): boolean {
  return isSuperAdminUserRole(userRole);
}

export function canDeleteRecordings(userRole?: string | null): boolean {
  return isSuperAdminUserRole(userRole);
}

export function canBypassModeration(userRole?: string | null): boolean {
  return isSuperAdminUserRole(userRole);
}

/** Pins / split-view style routes: superAdmin, limited admin, or (elsewhere) project owner. */
export function canAccessPlatformAppTools(userRole?: string | null): boolean {
  return hasPlatformOrgAccess(userRole);
}

export function canViewBudgetCostColumns(userRole?: string | null): boolean {
  return isSuperAdminUserRole(userRole);
}

export function canConfigureCompetitions(userRole?: string | null): boolean {
  return isSuperAdminUserRole(userRole);
}

export function canCreateWanderPacks(userRole?: string | null): boolean {
  return isSuperAdminUserRole(userRole);
}

export function canEditWorkStreamTabSettings(userRole?: string | null): boolean {
  return isSuperAdminUserRole(userRole);
}

/** Resolve session role from userRole and/or currentRole (common call-site pattern). */
export function resolveSessionRole(
  userRole?: string | null,
  currentRole?: string | null
): string | null | undefined {
  return userRole ?? currentRole;
}
